/**
 * TicketDetailDrawer — dettaglio di un ticket di assistenza lato operatore.
 *
 * Thread messaggi cliente/operatore, composer di risposta (firma
 * "Assistenza YumaOS"), cambio stato e priorità con salvataggio immediato
 * (updateDoc/transazione dirette, pattern console super_admin), tempi del
 * ticket (prima risposta, risoluzione, età) e allegati scaricabili.
 *
 * Le due metriche temporali sono SEPARATE e mai confuse:
 * - prima risposta = created_at → first_response_at (reattività);
 * - risoluzione    = created_at → resolved_at (soluzione del problema).
 */

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton,
  MenuItem, Stack, TextField, Typography, alpha,
} from '@mui/material';
import {
  AttachFile, Close as CloseIcon, Send as SendIcon,
} from '@mui/icons-material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import {
  MAX_MESSAGE_LENGTH, allegatoFileName, getAllegatoDownloadUrl,
  listTicketMessages, sendOperatorMessage, updateTicketPriorita, updateTicketStato,
} from '../../services/supportTicketsService';
import type {
  SupportTicket, SupportTicketMessage, SupportTicketPriorita, SupportTicketStato,
} from '../../types/supportTickets';
import {
  TICKET_CATEGORIA_LABELS, TICKET_PRIORITA_LABELS, TICKET_PRIORITA_ORDER,
  TICKET_PRIORITA_TONES, TICKET_STATO_LABELS, TICKET_STATO_ORDER,
  TICKET_STATO_TONES, formatDateTime, formatDurataMs, formatDurataTra, tsMillis,
} from './ticketLabels';

const DRAWER_WIDTH = 560;

interface Props {
  /** Ticket aperto nel drawer (null = drawer chiuso). */
  ticket: SupportTicket | null;
  onClose: () => void;
  /** Chiamata dopo una scrittura riuscita (il padre ricarica la coda). */
  onChanged: () => void;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

export const TicketDetailDrawer: React.FC<Props> = ({
  ticket, onClose, onChanged, showSnack,
}) => {
  const [messages, setMessages] = useState<SupportTicketMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [composerText, setComposerText] = useState('');
  const [sending, setSending] = useState(false);
  // Stato/priorità locali per resa immediata della select; la verità resta il
  // documento (il padre ricarica e il prop `ticket` si riallinea).
  const [stato, setStato] = useState<SupportTicketStato>('aperto');
  const [priorita, setPriorita] = useState<SupportTicketPriorita>('normale');
  const [savingStato, setSavingStato] = useState(false);
  const [savingPriorita, setSavingPriorita] = useState(false);

  const loadMessages = async (ticketId: string) => {
    setMessagesLoading(true);
    setMessagesError(null);
    try {
      setMessages(await listTicketMessages(ticketId));
    } catch (e) {
      console.error('[support-tickets] caricamento thread fallito', e);
      setMessagesError(e instanceof Error ? e.message : 'Errore caricamento thread');
    } finally {
      setMessagesLoading(false);
    }
  };

  // Reset + caricamento thread al cambio di ticket
  useEffect(() => {
    if (!ticket) {
      setMessages([]);
      setMessagesError(null);
      setComposerText('');
      return;
    }
    setStato(ticket.stato);
    setPriorita(ticket.priorita);
    setComposerText('');
    loadMessages(ticket.id);
  }, [ticket?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Riallinea le select quando il padre ricarica il documento
  useEffect(() => {
    if (ticket) {
      setStato(ticket.stato);
      setPriorita(ticket.priorita);
    }
  }, [ticket?.stato, ticket?.priorita]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatoChange = async (next: SupportTicketStato) => {
    if (!ticket || next === stato) return;
    const previous = stato;
    setStato(next);
    setSavingStato(true);
    try {
      await updateTicketStato(ticket.id, next);
      showSnack(`Stato aggiornato: ${TICKET_STATO_LABELS[next]}`, 'success');
      onChanged();
    } catch (e) {
      setStato(previous);
      showSnack(e instanceof Error ? e.message : 'Errore aggiornamento stato', 'error');
    } finally {
      setSavingStato(false);
    }
  };

  const handlePrioritaChange = async (next: SupportTicketPriorita) => {
    if (!ticket || next === priorita) return;
    const previous = priorita;
    setPriorita(next);
    setSavingPriorita(true);
    try {
      await updateTicketPriorita(ticket.id, next);
      showSnack(`Priorità aggiornata: ${TICKET_PRIORITA_LABELS[next]}`, 'success');
      onChanged();
    } catch (e) {
      setPriorita(previous);
      showSnack(e instanceof Error ? e.message : 'Errore aggiornamento priorità', 'error');
    } finally {
      setSavingPriorita(false);
    }
  };

  /**
   * Invia la risposta e, se richiesto, risolve il ticket nello stesso gesto.
   *
   * ⚠️ L'ORDINE non è indifferente: prima il messaggio, poi lo stato. Quando il ticket passa a
   * risolto, il server guarda l'ultimo messaggio del thread per sapere cosa consegnare a chi
   * l'aveva aperto: se lo stato cambiasse per primo, troverebbe ancora il messaggio del
   * cliente e manderebbe l'avviso neutro al posto della risposta appena scritta.
   *
   * ⚠️ Se lo stato non si riesce a cambiare, la risposta è comunque partita e va detto: un
   * errore che si porta dietro anche il messaggio inviato farebbe riscrivere tutto a mano.
   */
  const handleSend = async (ancheRisolvi = false) => {
    if (!ticket || !composerText.trim()) return;
    setSending(true);
    try {
      await sendOperatorMessage(ticket.id, composerText);
      setComposerText('');
      if (ancheRisolvi) {
        try {
          await updateTicketStato(ticket.id, 'risolto');
          setStato('risolto');
          showSnack('Risposta inviata e ticket risolto', 'success');
        } catch (e) {
          showSnack(
            `Risposta inviata, ma lo stato non è cambiato: ${e instanceof Error ? e.message : 'errore'}`,
            'error',
          );
        }
      } else {
        showSnack('Risposta inviata', 'success');
      }
      await loadMessages(ticket.id);
      onChanged();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Errore invio risposta', 'error');
    } finally {
      setSending(false);
    }
  };

  const daRispondere = !!ticket && ticket.last_message_by === 'cliente' && ticket.stato !== 'chiuso';
  // Età: dalla creazione alla chiusura, o a ORA se il ticket è ancora vivo
  const etaMs = ticket
    ? (() => {
      const createdMs = tsMillis(ticket.created_at);
      if (createdMs === null) return null;
      const endMs = tsMillis(ticket.closed_at) ?? Date.now();
      return endMs - createdMs;
    })()
    : null;

  return (
    <Drawer
      anchor="right"
      open={!!ticket}
      onClose={onClose}
      // Sopra l'AppBar della console (che sta a drawer+1 per la sidebar):
      // senza questo la testata del pannello finisce nascosta sotto la barra.
      sx={{ zIndex: (t) => t.zIndex.modal }}
      PaperProps={{ sx: { width: { xs: '100%', sm: DRAWER_WIDTH } } }}
    >
      {ticket && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* ------------------------------------------------ HEADER */}
          <Box sx={{ p: 3, pb: 2 }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                  <Box component="span" sx={{
                    fontFamily: tokens.font.mono,
                    fontSize: '0.78rem',
                    color: tokens.color.ash,
                    letterSpacing: '0.02em',
                  }}>
                    {ticket.numero}
                  </Box>
                  <SoftChip label={ticket.tenant_nome} tone="primary" />
                  {daRispondere && <SoftChip label="Da rispondere" tone="warning" />}
                </Stack>
                <Typography
                  sx={{
                    fontFamily: tokens.font.display,
                    fontWeight: 600,
                    fontSize: '1.3rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                    color: tokens.color.graphite,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {ticket.oggetto}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
                  <SoftChip label={TICKET_STATO_LABELS[stato]} tone={TICKET_STATO_TONES[stato]} />
                  <SoftChip label={TICKET_PRIORITA_LABELS[priorita]} tone={TICKET_PRIORITA_TONES[priorita]} />
                  <SoftChip label={TICKET_CATEGORIA_LABELS[ticket.categoria] ?? ticket.categoria} tone="info" />
                </Stack>
              </Box>
              <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
            </Stack>

            {/* Gestione: stato + priorità con salvataggio immediato */}
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <TextField
                select label="Stato" value={stato} fullWidth size="small"
                disabled={savingStato}
                onChange={(e) => handleStatoChange(e.target.value as SupportTicketStato)}
              >
                {TICKET_STATO_ORDER.map((s) => (
                  <MenuItem key={s} value={s}>{TICKET_STATO_LABELS[s]}</MenuItem>
                ))}
              </TextField>
              <TextField
                select label="Priorità" value={priorita} fullWidth size="small"
                disabled={savingPriorita}
                onChange={(e) => handlePrioritaChange(e.target.value as SupportTicketPriorita)}
              >
                {TICKET_PRIORITA_ORDER.map((p) => (
                  <MenuItem key={p} value={p}>{TICKET_PRIORITA_LABELS[p]}</MenuItem>
                ))}
              </TextField>
            </Stack>

            {/* Tempi del ticket — prima risposta e risoluzione SEPARATE */}
            <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
              <TicketTiming
                label="Prima risposta"
                value={ticket.first_response_at
                  ? formatDurataTra(ticket.created_at, ticket.first_response_at)
                  : 'in attesa'}
                pending={!ticket.first_response_at}
              />
              <TicketTiming
                label="Risoluzione"
                value={ticket.resolved_at
                  ? formatDurataTra(ticket.created_at, ticket.resolved_at)
                  : '—'}
                pending={!ticket.resolved_at}
              />
              <TicketTiming label="Età" value={formatDurataMs(etaMs)} />
            </Stack>

            <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 1.5 }}>
              Aperto da {ticket.created_by_nome} ({ticket.created_by_email}) · {formatDateTime(ticket.created_at)}
            </Typography>
          </Box>

          <Divider />

          {/* ------------------------------------------------ THREAD */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 3, pt: 2, bgcolor: tokens.color.bone }}>
            {/* Descrizione iniziale del cliente = primo blocco del thread */}
            <MessageBubble
              autoreTipo="cliente"
              autoreNome={ticket.created_by_nome}
              createdAt={ticket.created_at}
              testo={ticket.descrizione}
              allegati={ticket.allegati ?? []}
              showSnack={showSnack}
            />

            {messagesLoading ? (
              <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
            ) : messagesError ? (
              <Alert severity="error" sx={{ mt: 1.5 }}>{messagesError}</Alert>
            ) : (
              messages.map((m) => (
                <MessageBubble
                  key={m.id}
                  autoreTipo={m.autore_tipo}
                  autoreNome={m.autore_nome}
                  createdAt={m.created_at}
                  testo={m.testo}
                  allegati={m.allegati ?? []}
                  showSnack={showSnack}
                />
              ))
            )}
          </Box>

          <Divider />

          {/* ------------------------------------------------ COMPOSER */}
          <Box sx={{ p: 2.5, pt: 2 }}>
            <TextField
              placeholder="Scrivi una risposta al cliente…"
              value={composerText}
              onChange={(e) => setComposerText(e.target.value)}
              fullWidth multiline minRows={3} maxRows={8}
              inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                Rispondi come "Assistenza YumaOS" · {composerText.length}/{MAX_MESSAGE_LENGTH}
              </Typography>
              <Stack direction="row" spacing={1}>
                {/*
                  ⚠️ Rispondere e risolvere in un gesto solo, come nei sistemi di assistenza:
                  questa risposta è ciò che la persona leggerà dentro il gestionale quando il
                  ticket si chiude. Risolvendo senza scrivere, riceve un avviso neutro, mai un
                  messaggio vecchio riciclato fuori contesto.

                  ⚠️ Si risolve DOPO aver pubblicato la correzione: l'avviso raggiunge solo chi
                  ha già la versione online in quel momento.
                */}
                <Button
                  variant="outlined"
                  onClick={() => handleSend(true)}
                  disabled={!composerText.trim() || sending || stato === 'risolto' || stato === 'chiuso'}
                >
                  Invia e risolvi
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleSend(false)}
                  disabled={!composerText.trim() || sending}
                  startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
                >
                  {sending ? 'Invio…' : 'Invia risposta'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

// ============================================================================
// TICKET TIMING — metrica compatta (prima risposta / risoluzione / età)
// ============================================================================

const TicketTiming: React.FC<{ label: string; value: string; pending?: boolean }> = ({
  label, value, pending = false,
}) => (
  <Box sx={{ borderLeft: `2px solid ${tokens.color.line}`, pl: 1.25 }}>
    <Typography sx={{
      fontSize: '0.62rem',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: tokens.color.mist,
      lineHeight: 1,
    }}>
      {label}
    </Typography>
    <Typography sx={{
      fontFamily: tokens.font.mono,
      fontSize: '0.82rem',
      fontWeight: 600,
      color: pending ? tokens.color.mist : tokens.color.graphite,
      fontStyle: pending ? 'italic' : 'normal',
      mt: 0.4,
      fontVariantNumeric: 'tabular-nums',
    }}>
      {value}
    </Typography>
  </Box>
);

// ============================================================================
// MESSAGE BUBBLE — cliente a sinistra (carta), operatore a destra (tinta)
// ============================================================================

const MessageBubble: React.FC<{
  autoreTipo: 'cliente' | 'operatore';
  autoreNome: string;
  createdAt: unknown;
  testo: string;
  allegati: string[];
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}> = ({ autoreTipo, autoreNome, createdAt, testo, allegati, showSnack }) => {
  const isOperatore = autoreTipo === 'operatore';
  return (
    <Box sx={{
      display: 'flex',
      justifyContent: isOperatore ? 'flex-end' : 'flex-start',
      mt: 1.5,
    }}>
      <Box sx={{
        maxWidth: '85%',
        p: 1.5,
        borderRadius: 2,
        bgcolor: isOperatore
          ? alpha(tokens.color.tierBusiness.main, 0.08)
          : tokens.color.paper,
        border: `1px solid ${isOperatore
          ? alpha(tokens.color.tierBusiness.main, 0.18)
          : tokens.color.line}`,
      }}>
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{
            fontWeight: 700,
            color: isOperatore ? tokens.color.tierBusiness.main : tokens.color.ash,
          }}>
            {autoreNome}
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.color.mist }}>
            {formatDateTime(createdAt)}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {testo}
        </Typography>
        {allegati.length > 0 && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
            {allegati.map((path) => (
              <AllegatoChip key={path} storagePath={path} showSnack={showSnack} />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

// ============================================================================
// ALLEGATO — chip che risolve l'URL Storage on-demand e apre in nuova tab
// ============================================================================

const AllegatoChip: React.FC<{
  storagePath: string;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}> = ({ storagePath, showSnack }) => {
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    setLoading(true);
    try {
      const url = await getAllegatoDownloadUrl(storagePath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('[support-tickets] download allegato fallito', e);
      showSnack(
        e instanceof Error ? `Allegato non disponibile: ${e.message}` : 'Allegato non disponibile',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Chip
      size="small"
      icon={loading ? <CircularProgress size={12} /> : <AttachFile />}
      label={allegatoFileName(storagePath)}
      onClick={handleOpen}
      disabled={loading}
      sx={{
        height: 24,
        fontSize: '0.72rem',
        maxWidth: 220,
        '& .MuiChip-icon': { fontSize: 14 },
      }}
    />
  );
};
